import { baseApi } from '@/shared/api/baseApi';

import {
  annualReviewListSchema,
  annualReviewSchema,
  directorSignatureSchema,
  signatureListSchema,
  type AnnualReview,
  type AnnualReviewList,
  type DirectorSignature,
  type Signature,
} from './annualReview.schema';

interface CompanyArgs {
  asicCompanyId: string;
}

interface AnnualReviewArgs extends CompanyArgs {
  annualReviewId: string;
}

interface PayAnnualReviewArgs extends AnnualReviewArgs {
  transactionId: string;
  paidDate: number | string;
}

interface SolvencyUploadArgs extends AnnualReviewArgs {
  /** A `data:application/pdf;base64,...` URL. The API takes the document
   *  inline rather than as multipart, and stores this string as given. */
  pdfContent: string;
  pdfName: string;
}

interface ToggleViewArgs {
  asicCompanyId: string;
  documentId: string;
}

interface InviteDirectorsArgs {
  documentsId: string;
  invitedDirectors: {
    companyMemberId: string;
    invitedByMemberId: string;
    email: string;
  }[];
}

/**
 * Annual review, signing and signature endpoints.
 *
 * Signing is what the portal is for, so the invalidation here is deliberate:
 * anything that changes signing state invalidates `AnnualReview` for that
 * company, which is what makes the list reflect a signature without a manual
 * refetch at the call site.
 */
export const annualReviewApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAnnualReviews: build.query<AnnualReviewList, CompanyArgs>({
      query: ({ asicCompanyId }) => `/director/${asicCompanyId}/ar`,
      transformResponse: (response: unknown): AnnualReviewList =>
        annualReviewListSchema.parse(response),
      providesTags: (_result, _error, { asicCompanyId }) => [
        { type: 'AnnualReview', id: asicCompanyId },
      ],
    }),

    getAnnualReview: build.query<AnnualReview, AnnualReviewArgs>({
      query: ({ asicCompanyId, annualReviewId }) =>
        `/director/${asicCompanyId}/ar/${annualReviewId}`,
      transformResponse: (response: unknown): AnnualReview =>
        annualReviewSchema.parse(response),
      providesTags: (_result, _error, { annualReviewId }) => [
        { type: 'AnnualReview', id: annualReviewId },
      ],
    }),

    /** Records that the director paid ASIC directly, outside the portal. */
    recordAnnualPayment: build.mutation<undefined, PayAnnualReviewArgs>({
      query: ({ asicCompanyId, annualReviewId, ...body }) => ({
        url: `/director/${asicCompanyId}/ar/${annualReviewId}/payment`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { asicCompanyId, annualReviewId }) => [
        { type: 'AnnualReview', id: asicCompanyId },
        { type: 'AnnualReview', id: annualReviewId },
      ],
    }),

    uploadSolvencyDocument: build.mutation<undefined, SolvencyUploadArgs>({
      query: ({ asicCompanyId, annualReviewId, ...body }) => ({
        url: `/director/${asicCompanyId}/ar/${annualReviewId}/solvency`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { asicCompanyId, annualReviewId }) => [
        { type: 'AnnualReview', id: asicCompanyId },
        { type: 'AnnualReview', id: annualReviewId },
      ],
    }),

    /** Marks a document as seen. The API answers with the viewing member's id. */
    markDocumentViewed: build.mutation<unknown, ToggleViewArgs>({
      query: ({ asicCompanyId, documentId }) => ({
        url: `/director/${asicCompanyId}/ar/view/document/${documentId}`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (_result, _error, { asicCompanyId }) => [
        { type: 'AnnualReview', id: asicCompanyId },
      ],
    }),

    /** Who still has to sign a given document. */
    getDocumentSigners: build.query<DirectorSignature[], ToggleViewArgs>({
      query: ({ asicCompanyId, documentId }) =>
        `/director/${asicCompanyId}/document/${documentId}`,
      transformResponse: (response: unknown): DirectorSignature[] =>
        directorSignatureSchema.array().parse(response),
      providesTags: (_result, _error, { documentId }) => [
        { type: 'Signature', id: documentId },
      ],
    }),

    inviteDirectorsToSign: build.mutation<undefined, InviteDirectorsArgs>({
      query: (body) => ({ url: '/director/sign/invite', method: 'POST', body }),
      invalidatesTags: (_result, _error, { documentsId }) => [
        { type: 'Signature', id: documentsId },
      ],
    }),

    // --- The director's saved signature images ---

    getSignatures: build.query<Signature[], undefined>({
      query: () => '/director/signature/list',
      transformResponse: (response: unknown): Signature[] =>
        signatureListSchema.parse(response).signatures,
      providesTags: [{ type: 'Signature', id: 'LIST' }],
    }),

    createSignature: build.mutation<unknown, { signature: string }>({
      query: (body) => ({ url: '/director/signature', method: 'POST', body }),
      invalidatesTags: [{ type: 'Signature', id: 'LIST' }],
    }),

    deleteSignature: build.mutation<undefined, { signatureId: string }>({
      query: ({ signatureId }) => ({
        url: `/director/signature/${signatureId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Signature', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAnnualReviewsQuery,
  useGetAnnualReviewQuery,
  useRecordAnnualPaymentMutation,
  useUploadSolvencyDocumentMutation,
  useMarkDocumentViewedMutation,
  useGetDocumentSignersQuery,
  useInviteDirectorsToSignMutation,
  useGetSignaturesQuery,
  useCreateSignatureMutation,
  useDeleteSignatureMutation,
} = annualReviewApi;
